import React from "react";
import { Container, Row, Col, Card, Button, Image, Badge } from "react-bootstrap";
import { FaUsers, FaHeart, FaStar, FaDollarSign } from "react-icons/fa";
import leaderPlaceholder from "../assets/leader-placeholder.jpg";
import heroImage from "../assets/hero-about.jpg";

const leaders = [
  { name: "Elizabeth Mutua", role: "Chairperson", description: "Oversees all group activities and strategic direction.", years: "2022-Present" },
  { name: "Charles Kimweli", role: "Chairperson/Accountant", description: "Co-leads the organization and manages financial accountability.", years: "2021-Present" },
  { name: "Grace Mwaniki", role: "Treasurer", description: "Handles financial records and bereavement contributions.", years: "2021-Present" },
  { name: "Nancy Ndinda", role: "Secretary", description: "Keeps records, documentation and meeting minutes.", years: "2022-Present" },
  { name: "Margarita Mueni", role: "Events Coordinator", description: "Plans and executes community events and gatherings.", years: "2023-Present" },
  { name: "Lenny Mutua", role: "Public Relations", description: "Manages communication, outreach and member relations.", years: "2021-Present" },
  { name: "Tabitha Ogada", role: "Events Coordinator", description: "Collaborates on event planning and community engagement.", years: "2023-Present" },
];

const initiatives = [
  { title: "Bereavement Support", description: "Providing immediate financial and emotional support to families during times of loss, eliminating the burden of emergency fundraising.", icon: "🤝", stat: "$25K+ Raised" },
  { title: "Cultural Preservation", description: "Celebrating and preserving Kamba traditions through festivals, language classes, and cultural events that connect us to our heritage.", icon: "🎉", stat: "12+ Events/Year" },
  { title: "Community Networking", description: "Building professional and social networks that create opportunities for career advancement and business partnerships.", icon: "🌐", stat: "45+ Members" },
  { title: "Family Integration", description: "Helping families integrate into American society while maintaining cultural identity through mentorship and support programs.", icon: "👨‍👩‍👧‍👦", stat: "100+ Families" },
];

const About = () => {
  return (
    <>
      {/* Enhanced Hero Section */}
      <section
        className="hero-section position-relative d-flex align-items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "70vh",
          marginTop: "-80px",
          paddingTop: "80px",
          textShadow: "2px 2px 8px rgba(0,0,0,0.7)"
        }}
      >
        <Container className="text-center text-white">
          <Row className="justify-content-center">
            <Col lg={10}>
              <Badge bg="warning" className="mb-3 fs-6 px-3 py-2">OUR STORY</Badge>
              <h1 className="fw-bold display-3 mb-4">About Mbaitu USA</h1>
              <p className="lead fs-4 mb-4">
                Founded with a vision to unite the Kamba community in Texas, we are more than an organization —
                we are a family dedicated to preserving our culture while embracing our American future.
              </p>
              <div className="mt-4">
                <Button variant="warning" href="/contact" className="me-3 shadow-lg btn-lg px-4">
                  Learn How to Join
                </Button>
                <Button variant="outline-light" href="/events" className="btn-lg px-4 btn-outline-thick">
                  Our Community Events
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Story Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4">
              <Card className="border-0 shadow-sm p-4 bg-white card-hover">
                <h4 className="fw-bold text-primary mb-3">Our Story</h4>
                <p className="text-muted mb-3">
                  Mbaitu USA was born from the shared experiences of Kamba families who found themselves
                  navigating life in Texas while staying connected to their roots. What started as informal
                  gatherings has grown into a structured community organization.
                </p>
                <p className="text-muted mb-3">
                  We recognized that traditional approaches to community support, particularly the frequent
                  emergency fundraising for bereavements, were placing unnecessary stress on families already
                  adapting to life in a new country.
                </p>
                <p className="text-muted mb-0">
                  Today, we stand as a testament to what can be achieved when a community comes together
                  with shared purpose and vision.
                </p>
              </Card>
            </Col>
            <Col lg={6}>
              <h2 className="fw-bold mb-4 section-heading">
                Mission & Vision
              </h2>
              <div className="mb-4">
                <div className="d-flex align-items-start mb-3">
                  <div className="p-3 bg-warning bg-opacity-25 rounded-3 me-3">
                    <FaHeart className="text-warning" size={24} />
                  </div>
                  <div>
                    <h5 className="fw-bold text-primary">Our Mission</h5>
                    <p className="text-muted">
                      To unite the Kamba community in DFW and Texas, creating sustainable solutions
                      to eliminate frequent burial fundraisers while promoting a vibrant social,
                      cultural, and economic foundation for all members.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-start">
                  <div className="p-3 bg-warning bg-opacity-25 rounded-3 me-3">
                    <FaStar className="text-warning" size={24} />
                  </div>
                  <div>
                    <h5 className="fw-bold text-primary">Our Vision</h5>
                    <p className="text-muted">
                      To reduce the impact of social burdens and accelerate the cultural integration
                      of the Kamba community within broader American society, ensuring a brighter
                      future for current and future generations.
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Community Initiatives & Programs */}
      <Container className="py-5">
        <section className="initiatives-section py-5">
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="fw-bold mb-4 section-heading">What We Do</h2>
              <p className="text-muted fs-5">
                Our programs are designed to create sustainable solutions for community challenges while
                celebrating our heritage and strengthening family bonds.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            {initiatives.map((initiative, index) => (
              <Col lg={6} key={index} className="mb-4">
                <Card className="h-100 shadow-sm border-0 p-4 card-hover">
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-warning bg-opacity-25 rounded-3 me-3">
                      <span style={{fontSize: "24px"}}>{initiative.icon}</span>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="fw-bold text-primary mb-0">{initiative.title}</h5>
                        <Badge bg="success" className="ms-2">{initiative.stat}</Badge>
                      </div>
                      <p className="text-muted mb-0">{initiative.description}</p>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* Enhanced Leadership Team */}
        <section className="leadership-section py-5 bg-light">
          <Container>
            <Row className="text-center mb-5">
              <Col lg={8} className="mx-auto">
                <h2 className="fw-bold mb-4 section-heading">Our Leadership Team</h2>
                <p className="text-muted fs-5">
                  Meet the dedicated volunteers who guide our community with passion,
                  wisdom, and commitment to our shared values.
                </p>
              </Col>
            </Row>
            <Row className="g-4">
              {leaders.map((leader, index) => (
                <Col key={index} lg={4} md={6} className="mb-4">
                  <Card className="h-100 shadow-sm border-0 text-center p-4 leader-card">
                    <div className="position-relative mb-3">
                      <Image
                        src={leaderPlaceholder}
                        alt={leader.name}
                        className="rounded-circle mx-auto"
                        style={{width: "100px", height: "100px", objectFit: "cover"}}
                      />
                      <Badge
                        bg="warning"
                        className="position-absolute bottom-0 end-0"
                        style={{transform: "translate(-10px, -10px)"}}
                      >
                        {leader.years}
                      </Badge>
                    </div>
                    <h5 className="fw-bold text-dark">{leader.name}</h5>
                    <Badge bg="primary" className="mb-3">{leader.role}</Badge>
                    <p className="text-muted small mb-0">{leader.description}</p>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Membership Information - Enhanced */}
        <section className="membership-section py-5">
          <Container>
            <Row className="text-center mb-5">
              <Col lg={8} className="mx-auto">
                <h2 className="fw-bold mb-4 section-heading">Join Mbaitu USA</h2>
                <p className="text-muted fs-5">
                  Become part of a supportive community that stands together through life's journey.
                </p>
              </Col>
            </Row>
            <Row className="g-4">
              <Col lg={6}>
                <Card className="h-100 shadow-sm border-0 p-4 card-hover">
                  <div className="text-center mb-4">
                    <div className="p-3 bg-primary bg-opacity-10 rounded-circle d-inline-block mb-3">
                      <FaUsers className="text-primary" size={32} />
                    </div>
                    <h4 className="fw-bold text-primary">Membership Eligibility</h4>
                  </div>
                  <ul className="list-unstyled">
                    <li className="mb-2">Anyone of Kamba descent</li>
                    <li className="mb-2">Spouses married into the Kamba community</li>
                    <li className="mb-2">Community supporters who share our vision</li>
                    <li className="mb-2">Must be 18 years or older</li>
                    <li className="mb-2">Residing in Texas or surrounding states</li>
                  </ul>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100 shadow-sm border-0 p-4 card-hover">
                  <div className="text-center mb-4">
                    <div className="p-3 bg-warning bg-opacity-10 rounded-circle d-inline-block mb-3">
                      <FaDollarSign className="text-warning" size={32} />
                    </div>
                    <h4 className="fw-bold text-warning">Membership Investment</h4>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                      <span className="fw-bold">Registration Fee:</span>
                      <Badge bg="primary" className="fs-6">$300 (one-time)</Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                      <span className="fw-bold">Annual Membership:</span>
                      <Badge bg="success" className="fs-6">$50 (yearly)</Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                      <span className="fw-bold">Bereavement Support:</span>
                      <Badge bg="warning" className="fs-6">$60 per case</Badge>
                    </div>
                  </div>
                  <small className="text-muted">Annual dues collected January - March</small>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-5 bg-light">
          <Container>
            <Row className="text-center mb-5">
              <Col lg={8} className="mx-auto">
                <h2 className="fw-bold mb-4 section-heading">Why Choose Mbaitu USA?</h2>
                <p className="text-muted fs-5">
                  We offer more than membership — we provide a pathway to sustainable community living.
                </p>
              </Col>
            </Row>
            <Row className="g-4">
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center p-4 h-100 card-hover">
                  <div className="mb-3">
                    <span style={{fontSize: "48px"}}>🔒</span>
                  </div>
                  <h5 className="fw-bold text-primary">Financial Security</h5>
                  <p className="text-muted small">
                    Eliminate the stress of emergency fundraising with our systematic bereavement support fund.
                  </p>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center p-4 h-100 card-hover">
                  <div className="mb-3">
                    <span style={{fontSize: "48px"}}>🌍</span>
                  </div>
                  <h5 className="fw-bold text-primary">Cultural Connection</h5>
                  <p className="text-muted small">
                    Stay rooted in Kamba traditions while building bridges to American society.
                  </p>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center p-4 h-100 card-hover">
                  <div className="mb-3">
                    <span style={{fontSize: "48px"}}>🤝</span>
                  </div>
                  <h5 className="fw-bold text-primary">Genuine Support</h5>
                  <p className="text-muted small">
                    Experience the power of community during life's most challenging and celebratory moments.
                  </p>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Call to Action */}
        <section className="py-5 section-navy-gradient">
          <Container>
            <Row className="text-center text-white">
              <Col lg={8} className="mx-auto">
                <h2 className="fw-bold mb-4">Take the Next Step</h2>
                <p className="fs-5 mb-4">
                  Whether you're interested in membership, volunteering, or simply learning more about our community,
                  we invite you to connect with us.
                </p>
                <Row className="justify-content-center g-3">
                  <Col md={4}>
                    <Button
                      variant="warning"
                      href="/contact"
                      size="lg"
                      className="w-100 fw-bold shadow"
                    >
                      Contact Us
                    </Button>
                  </Col>
                  <Col md={4}>
                    <Button
                      variant="outline-light"
                      href="/events"
                      size="lg"
                      className="w-100 fw-bold btn-outline-thick"
                    >
                      Attend a Meeting
                    </Button>
                  </Col>
                  <Col md={4}>
                    <Button
                      variant="outline-light"
                      href="/financials"
                      size="lg"
                      className="w-100 fw-bold btn-outline-thick"
                    >
                      See Our Impact
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </section>
      </Container>
    </>
  );
};

export default About;
